import config from '../config';
import {
  authHeader,
  customFetch,
  commonRequestOptions,
  handleResponse,
  jsonHeader,
  subjects,
} from '../helpers';
import { attributeService } from './Attribute.service';

const endpointPath = config.api.API_URL + '/attribute_value';

export const attributeValueService = {
  create,
  destroy,
  getAll,
  getByAttributeId,
  getById,
  update,
};

async function update(items, signal) {
  const requestOptions = {
    method: 'PUT',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };
  return customFetch(endpointPath, requestOptions, signal).then(handleResponse);
}

async function getById(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: { ...authHeader() },
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/${id}`, requestOptions, signal)
    .then(handleResponse)
    .then(res => {
      const value = res.attribute_values[0];
      subjects.currentAttributeValue.next(value || { attribute_value_id: id });
      // The value's own crumb sits under its attribute, so the attribute is published from here too.
      // The glossary is part of it: without one the trail cannot show where the asset lives, and the
      // value page is reached directly often enough (a link, a bookmark) that it cannot rely on the
      // attribute page having published a fuller row earlier.
      subjects.currentAttribute.next(
        value
          ? {
              attribute_id: value.attribute_id,
              attribute_name: value.attribute_name,
              attribute_role_name: value.attribute_role_name,
              attribute_role_id: value.attribute_role_id,
              glossary_id: value.glossary_id,
              glossary_name: value.glossary_name,
              parent_entity_id: value.parent_entity_id,
            }
          : 'not_found'
      );

      // The entity chain above the attribute, so a value gets the same trail its attribute has. Fetched
      // after the value rather than alongside it because the attribute is only known once the value has
      // been read; the crumbs slide in when it arrives, as the dataset and entity padding also do.
      if (value?.attribute_id) {
        attributeService
          .getAncestors(value.attribute_id)
          .then(ancestorsRes =>
            subjects.currentAttributeAncestors.next({
              attribute_id: value.attribute_id,
              ancestors: ancestorsRes.entities,
            })
          )
          .catch(() =>
            subjects.currentAttributeAncestors.next({
              attribute_id: value.attribute_id,
              ancestors: [],
            })
          );
      }

      return res;
    });
}

async function destroy(items, signal) {
  const requestOptions = {
    method: 'DELETE',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

async function create(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(endpointPath, requestOptions, signal).then(handleResponse);
}

async function getByAttributeId(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };

  return customFetch(`${endpointPath}/attribute/${id}`, requestOptions, signal)
    .then(handleResponse)
    .then(res => {
      subjects.currentAttributeValue.next(res.attribute_values[0] || { attribute_value_id: id });
      return res;
    });
}

/**
 * Sends a GET request for attribute values, optionally narrowed by query parameters.
 *
 * Unlike getByAttributeId this publishes nothing: it is the read a list uses, where "the current
 * attribute value" is not a thing that exists, and announcing one would move the breadcrumb trail to
 * an asset the user never opened.
 *
 * @param {string} [query] - Query string without the leading "?", e.g. `$filter=attribute_id eq 7`.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ message: string, attribute_values: object[] }>}
 */
async function getAll(query, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/all${query ? `?${query}` : ''}`, requestOptions, signal).then(
    handleResponse
  );
}
